import {ListQuestion} from '../models/list-question';
import { Injectable,Inject } from '@angular/core';
import { Scale } from "../models/scale";
import { ScaleRange } from "../models/scale-range";
import { Assessment } from "../models/assessment";
import { QuestionSet } from "../models/question-set";
import { QuestionSetLabel } from "../models/question-set-label";
import { Question } from "../models/question";
import { DisplayCondition } from "../models/display-condition";
import { QuestionFactoryService } from "./question-factory.service";
import {extend, omit} from "lodash";
import { FlaggedCondition } from 'app/shared/models/flagged-condition';

@Injectable()
export class AssessmentHydrator
{
	private scales:Map<String, Scale>;
	
	constructor(
		private questionFactory:QuestionFactoryService) {
    this.scales = new Map();
	}
	
	public hydrate(data: any) : Assessment {
		let assessment = new Assessment();
		extend(assessment, data);
		
		
		let qs = data._question_sets || data.question_sets;
		let scales = data._scales || data.scales; 
		if (scales) {
			// we need to hydrate the scale, then populate the map, finally hydrate the sub scales as they may refer to each other
			// since we likely won't have more than 20-50 items this should be fine for runtime.
			assessment.scales = scales.map(s => this.hydrateScale(s));
			assessment.scales.forEach(s => this.scales[s.id] = s);
			assessment.scales.forEach(s => this.populateSubScaleProxies(s));
		}
		
		if (qs) {
			assessment.question_sets = qs.map(s => this.hydrateQuestionSet(s));
			this.hydrateDisplayConditions(assessment);
		}
		if (this.shouldMigrateAssessment(assessment.uid)) {
			this.migrateAssessment(assessment);
		}
		return assessment;
	}

	private shouldMigrateAssessment(uid:string) {
		let migrateAssessments = ['perceived-social-support', 'couples-satisfaction-index'];
		if (migrateAssessments.indexOf(uid) >= 0) {
			return true;
		}
		return false;
	}
	private migrateAssessment(assessment:Assessment) {
		// force the couples-satisfaction-index to have the negative to positive flag
		let negToPosAssessments = ['perceived-social-support', 'couples-satisfaction-index'];
		if (negToPosAssessments.indexOf(assessment.uid) >= 0) {
			assessment.scales.forEach(s => s.negativeToPositiveRanges = true);
		}
	}

	private hydrateDisplayConditions(assessment:Assessment) {
		assessment.question_sets.forEach(qs => {
			if (qs.display_conditions.length) {
				qs.display_conditions = qs.display_conditions.map((dc:any) => {
					let result = new DisplayCondition();
					result.displayType = dc._displayType;
					result.questionSet = assessment.getQuestionSetById(dc._questionSetId);
					if (result.questionSet) {
						result.question = result.questionSet.getQuestionById(dc._questionId);
						if (result.question instanceof ListQuestion && result.question.CustomLabels) {
							result.answer = result.question.CustomLabels.find(l => l.id == dc._answerId);
						}
						// try looking in the questionSet if we don't have anything with the question.
						if (!result.answer) {
							result.answer = result.questionSet.labels.find(l => l.id == dc._answerId);
						}
					}
					return result;
				});
			}
		});
	}
	
	private hydrateQuestionSet(data:any) : QuestionSet{
		let item:QuestionSet = extend(new QuestionSet(), data);
		let labels = data._labels || data.labels;
		let questions = data._questions || data.questions;
		let display_conditions = data._display_conditions || data.display_conditions;
		
		if (labels) {
			let newLabels = labels.map(i => this.hydrateQuestionSetLabel(i));
			item.labels = newLabels;
		}
		if (questions) {
			let newQuestions = questions.map(i => this.hydrateQuestion(i, item));
			item.questions = newQuestions;
		}
		if (display_conditions) {
			item.display_conditions = display_conditions;
		}
		return item;
	}
	
	private hydrateQuestionSetLabel(data:any) : QuestionSetLabel {
		return extend(new QuestionSetLabel(), data);
	}
	
	public hydrateScaleRange(data:any) : ScaleRange {
		return extend(new ScaleRange(), data);
	}
	
	public hydrateScale(data:any) : Scale {
		let scale:Scale = extend(new Scale(), data);
		let ranges = data._ranges || data.ranges;
		if (ranges) {
			scale.ranges = ranges.map(r => this.hydrateScaleRange(r));
		}
		let scaleIds = data._subScaleIds || data.subScaleIds;
		if (scaleIds && scaleIds.length > 0) {
			// create proxy objects that we will fill in once everything is done.
			scaleIds.map(id => Object.assign(new Scale(), {id: id}))
				.forEach(s => {
					if (s) {
						scale.addSubScale(s);
					}
				});
		}
		return scale;
	}

	private populateSubScaleProxies(scale:Scale) {
		for (let id of scale.getSubScaleIds()) {
			let proxy = scale.SubScales[id];
			if (this.scales[id]) { // replace the sub scale with the hydrated one
				scale.addSubScale(this.scales[id]);
			}
			else {
				console.error("Failed to find sub scale with id " + id + " in scale " + scale.id);
			}
		}
	}
	
	private hydrateQuestion(data:any, questionSet:QuestionSet) : Question {
		let type = data.type || data._type;
		let question = this.questionFactory.createQuestionForType(type);
		var item:Question = extend(question, omit(data, '_answers')); // skip over the _answers for the QuestionSet Answer
		// let answer = data._answer || data.answer;
		let scale = data._scale || data.scale;
		let flaggedCondition = data._flaggedCondition || data.flaggedCondition;
		let customLabels = data._customLabels || data.customLabels;
		
		if (scale) {
			// grab the scale from our populated scales
			// so we can deal with the reference pointers
			let scaleId = scale._id || scale.id;
			if (this.scales[scaleId]) {
				item.assessment_scale = this.scales[scaleId];
			}
		}
		if (flaggedCondition) {
			let flag:FlaggedCondition = extend(new FlaggedCondition(), flaggedCondition);
			// overwrite the labels so we use what's in the question set.
			let lbls = questionSet.labels;
			let flagValues = flaggedCondition._flagValues || flaggedCondition.flagValues;
			flag.clearFlagValues();
			flagValues.forEach(fv => {
				let id = fv.id || fv._id;
				let qsl = questionSet.labels.find(qsl => id == qsl.id);
				if (qsl) {
					flag.addFlagValue(qsl)
				}
				else {
					console.log("question set did not have label with id ", fv.id);
				}
			});
			question.flaggedCondition = flag;
		}
		// TODO: stephen not sure I like this as there's too much type checking here.
		if (customLabels) {
			let labels = customLabels.map(cl => extend(new QuestionSetLabel(), cl));
			let listType = question as ListQuestion;
			listType.CustomLabels = labels;
		}
		return item;
	}
}


// WEBPACK FOOTER //
// ./src/app/shared/services/assessment-hydrator.service.ts